import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';

import {
  useGetAccountSettingQuery,
  useUpdateAccountSettingMutation,
  useUpdateAccountUserAddedEmailNotificationSettingMutation,
} from 'generated/license.generated';
import type {
  UpdateAccountSettingMutation,
  UpdateAccountUserAddedEmailNotificationSettingMutation,
} from 'generated/license.generated';
import type {
  UpdateAccountSettingInput,
  UpdateAccountUserAddedEmailNotificationSettingInput,
} from 'generated/types';

export function useUpdateAccountSettings(): [
  (
    params: UpdateAccountSettingInput
  ) => Promise<FetchResult<UpdateAccountSettingMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
  });
  const [mutate, { loading: baseLoading }] = useUpdateAccountSettingMutation();
  const loading = baseLoading;

  async function updateAccountSetting(params: UpdateAccountSettingInput) {
    const response = await mutate({
      variables: {
        input: { ...params },
      },
    });

    const { data, errors } = response;

    const action =
      params.branding_color_primary === undefined ||
      params.branding_color_secondary === undefined
        ? 'updateAccountSetting'
        : !params.branding_color_primary && !params.branding_color_secondary
          ? 'deleteColors'
          : 'uploadColors';

    if (errors) {
      handleError(errors, action);
    } else if (data?.updateAccountSetting) {
      handleSuccess(action);
    }

    return response;
  }

  return [updateAccountSetting, loading];
}

export function useUpdateAccountUserAddedEmailNotificationSetting(): [
  (
    params: UpdateAccountUserAddedEmailNotificationSettingInput
  ) => Promise<
    FetchResult<UpdateAccountUserAddedEmailNotificationSettingMutation>
  >,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
  });
  const [mutate, { loading: baseLoading }] =
    useUpdateAccountUserAddedEmailNotificationSettingMutation();
  const loading = baseLoading;

  async function updateAccountUserAddedEmailNotificationSetting(
    params: UpdateAccountUserAddedEmailNotificationSettingInput
  ) {
    const response = await mutate({
      variables: {
        input: { ...params },
      },
    });

    const { data, errors } = response;
    const action = 'updateAccountSetting';

    if (errors) {
      handleError(errors, action);
    } else if (data?.updateAccountUserAddedEmailNotificationSetting) {
      handleSuccess(action);
    }

    return response;
  }

  return [updateAccountUserAddedEmailNotificationSetting, loading];
}

export function useAccountSettings(account_id: string) {
  const { data, loading, error } = useGetAccountSettingQuery({
    variables: { input: { account_id } },
    skip: !account_id,
  });

  return {
    accountSettings: data,
    loading,
    error,
  };
}
